import { useMemo, useState } from 'react';
import {
  Card,
  Table,
  Tag,
  Space,
  Select,
  Input,
  Row,
  Col,
  Statistic,
  Progress,
  Modal,
  Empty,
  Spin,
  Typography,
  Button,
  Avatar,
  Alert,
  Tooltip,
  message,
} from 'antd';
import {
  CheckCircleTwoTone,
  ClockCircleTwoTone,
  TeamOutlined,
  UserOutlined,
  FileSearchOutlined,
  CheckCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { useBatches } from '../hooks/useBatches';
import { useEvalRoleLabels } from '../contexts/EvalRoleLabelsContext';
import { useTalentPoolLabels } from '../contexts/TalentPoolLabelsContext';
import {
  adminPageWrap,
  adminAlertStyle,
  adminFilterCardStyle,
  adminContentCardStyle,
} from '../styles/adminPageShell';
import { taskStatusLabel, taskStatusColor } from '../utils/taskStatusDisplay';

const { Text } = Typography;

const POOL_COLORS: Record<string, string> = {
  EXECUTIVE: 'red',
  MIDDLE_MGMT: 'volcano',
  BACKEND: 'blue',
  FINANCE_DATA: 'cyan',
  MARKETING_ACQUISITION: 'purple',
  SALES: 'gold',
  MEDICAL_OPS: 'green',
};

type DoneFilter = 'DONE' | 'TODO';
const DONE_STATUSES = new Set(['SUBMITTED', 'LOCKED']);
const isDone = (status: string) => DONE_STATUSES.has(status);

/** 一条维度评分（来自 EvaluationScore 表，与模板里的维度 code 对齐）。 */
interface ScoreRow {
  dimCode: string;
  score: number;
  comment?: string | null;
}

/** 模板里配置的维度信息（用于把 dimCode 映射成中文名 / 锚点描述）。 */
interface TemplateDim {
  code: string;
  name?: string;
  desc?: string;
  anchors?: string[];
}

interface RawTask {
  id: number;
  evaluator: { id: number; name: string };
  evaluatee: { id: number; name: string; deptPath?: string | null };
  template: { pool: string; dimensions?: TemplateDim[] | null };
  roleInEval: string;
  status: string;
  submitDuration?: number | null;
  submitAt?: string | null;
  scores?: ScoreRow[];
}

/** 客户端按被评价人聚合一行（每人一行：N 名评价人 → 已完成多少 / 完成率） */
interface EvaluateeRow {
  evaluateeId: number;
  evaluateeName: string;
  deptPath: string | null;
  pool: string;
  total: number;
  done: number;
  pending: number;
  percent: number;
  tasks: RawTask[];
}

function shortDept(path: string | null | undefined) {
  if (!path) return '-';
  const parts = path.split(/[\/\\>›→]/).map((s) => s.trim()).filter(Boolean);
  if (parts.length <= 2) return parts.join(' / ');
  return parts.slice(-2).join(' / ');
}

export function EvalTasksPage() {
  const { getEvalRoleLabel } = useEvalRoleLabels();
  const { getTalentPoolLabel, displayOrder } = useTalentPoolLabels();
  const { batches, batchId, setBatchId, isLoading: loadingBatches } = useBatches();
  const [doneFilter, setDoneFilter] = useState<DoneFilter | undefined>();
  const [poolFilter, setPoolFilter] = useState<string | undefined>();
  const [keyword, setKeyword] = useState('');
  const [activeRow, setActiveRow] = useState<EvaluateeRow | null>(null);
  const queryClient = useQueryClient();

  const { data = [], isLoading } = useQuery<RawTask[]>({
    queryKey: ['eval-tasks-admin', { batchId }],
    queryFn: () => api.get('/admin/eval-tasks', { params: { batchId } }),
    enabled: !!batchId,
  });

  const { data: calibrationResp } = useQuery<{ byEvaluateeId: Record<string, boolean> }>({
    queryKey: ['quality-calibration-reviews', batchId],
    queryFn: () =>
      api.get('/admin/quality-calibration-reviews', { params: { batchId } }),
    enabled: !!batchId,
  });

  const calibrationByEvaluatee = useMemo(() => {
    const src = calibrationResp?.byEvaluateeId || {};
    const out = new Map<number, boolean>();
    for (const [k, v] of Object.entries(src)) {
      const id = Number(k);
      if (Number.isFinite(id)) out.set(id, !!v);
    }
    return out;
  }, [calibrationResp]);

  const rowCalibrationMutation = useMutation({
    mutationFn: (vars: { evaluateeId: number; completed: boolean }) =>
      api.post('/admin/quality-calibration-reviews', {
        batchId,
        evaluateeId: vars.evaluateeId,
        completed: vars.completed,
      }),
    onMutate: async (vars) => {
      if (batchId == null) return {};
      const qk = ['quality-calibration-reviews', batchId] as const;
      await queryClient.cancelQueries({ queryKey: qk });
      const prev = queryClient.getQueryData<{ byEvaluateeId: Record<string, boolean> }>(qk);
      queryClient.setQueryData<{ byEvaluateeId: Record<string, boolean> }>(qk, (old) => ({
        byEvaluateeId: {
          ...(old?.byEvaluateeId || {}),
          [String(vars.evaluateeId)]: vars.completed,
        },
      }));
      return { prev, qk };
    },
    onError: (e: any, _v, ctx) => {
      if (ctx?.qk != null && 'prev' in ctx) {
        queryClient.setQueryData(ctx.qk, ctx.prev);
      }
      const m = e?.response?.data?.message;
      const text =
        typeof m === 'string'
          ? m
          : Array.isArray(m)
            ? m.map(String).join('；')
            : m != null
              ? JSON.stringify(m)
              : e?.message || '更新失败';
      message.error(text);
    },
    onSuccess: () => {
      message.success('已更新校准状态');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['quality-calibration-reviews'] });
    },
  });

  /** 按被评价人聚合（一名员工通常有多名评价人：自评/直属/同事…） */
  const grouped: EvaluateeRow[] = useMemo(() => {
    const map = new Map<number, EvaluateeRow>();
    for (const t of data) {
      const eId = t.evaluatee.id;
      let row = map.get(eId);
      if (!row) {
        row = {
          evaluateeId: eId,
          evaluateeName: t.evaluatee.name,
          deptPath: t.evaluatee.deptPath ?? null,
          pool: t.template?.pool || '',
          total: 0,
          done: 0,
          pending: 0,
          percent: 0,
          tasks: [],
        };
        map.set(eId, row);
      }
      row.total += 1;
      if (isDone(t.status)) row.done += 1;
      else row.pending += 1;
      row.tasks.push(t);
    }
    const rows = Array.from(map.values());
    rows.forEach((r) => {
      r.percent = r.total === 0 ? 0 : Math.round((r.done * 100) / r.total);
    });
    rows.sort((a, b) => a.percent - b.percent || a.evaluateeName.localeCompare(b.evaluateeName, 'zh-CN'));
    return rows;
  }, [data]);

  const stats = useMemo(() => {
    const totalEvaluatees = grouped.length;
    const fullyDone = grouped.filter((r) => r.percent === 100).length;
    const totalTasks = grouped.reduce((s, r) => s + r.total, 0);
    const submittedTasks = grouped.reduce((s, r) => s + r.done, 0);
    const pendingTasks = totalTasks - submittedTasks;
    const overallPercent = totalTasks ? Math.round((submittedTasks * 100) / totalTasks) : 0;
    return { totalEvaluatees, fullyDone, totalTasks, submittedTasks, pendingTasks, overallPercent };
  }, [grouped]);

  const filtered = useMemo(() => {
    const kw = keyword.trim();
    return grouped.filter((r) => {
      if (doneFilter === 'DONE' && r.percent !== 100) return false;
      if (doneFilter === 'TODO' && r.percent === 100) return false;
      if (poolFilter && r.pool !== poolFilter) return false;
      if (kw && !r.evaluateeName.includes(kw) && !(r.deptPath || '').includes(kw)) return false;
      return true;
    });
  }, [grouped, keyword, doneFilter, poolFilter]);

  return (
    <div style={adminPageWrap}>
      <div className="tg-page-title">素质项 · 评价进度</div>

      <Alert
        type="info"
        showIcon
        message="按被评价人维度查看评价进度"
        description="一行一名被评价人，统计他/她应被评价的全部评价人数（含自评/直属/同事）以及已提交多少。点「查看详情」可以看到具体的每位评价人、关系、状态和提交耗时。"
        style={{ ...adminAlertStyle, marginTop: 8, marginBottom: 12 }}
      />

      {/* 筛选 + 顶部 5 项汇总（与岗位胜任力派单同形态） */}
      <Card style={{ ...adminFilterCardStyle, marginBottom: 12 }} styles={{ body: { paddingBottom: 12 } }}>
        <Space wrap size={12}>
          <span>批次：</span>
          <Select
            placeholder="选择批次"
            style={{ width: 220 }}
            options={batches.map((b) => ({ label: b.name, value: b.id }))}
            onChange={setBatchId}
            value={batchId}
            loading={loadingBatches}
          />
          <span style={{ marginLeft: 8 }}>状态：</span>
          <Select
            placeholder="状态"
            allowClear
            style={{ width: 130 }}
            options={[
              { label: '已完成', value: 'DONE' },
              { label: '未完成', value: 'TODO' },
            ]}
            onChange={(v) => setDoneFilter(v as DoneFilter | undefined)}
            value={doneFilter}
          />
          <span style={{ marginLeft: 8 }}>人才池：</span>
          <Select
            placeholder="人才池"
            allowClear
            style={{ width: 140 }}
            options={displayOrder.map((k) => ({ value: k, label: getTalentPoolLabel(k) }))}
            onChange={setPoolFilter}
            value={poolFilter}
          />
          <Input.Search
            placeholder="搜索被评价人 / 部门"
            allowClear
            style={{ width: 220 }}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
        </Space>

        <Row gutter={16} style={{ marginTop: 12 }}>
          <Col span={5}>
            <Statistic
              title="被评价人"
              value={stats.totalEvaluatees}
              prefix={<TeamOutlined style={{ color: '#722ed1' }} />}
              suffix="人"
            />
          </Col>
          <Col span={5}>
            <Statistic
              title="已全部完成"
              value={stats.fullyDone}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleTwoTone twoToneColor="#52c41a" />}
              suffix="人"
            />
          </Col>
          <Col span={5}>
            <Statistic title="评价任务总数" value={stats.totalTasks} suffix="条" />
          </Col>
          <Col span={5}>
            <Statistic
              title="已提交任务"
              value={stats.submittedTasks}
              valueStyle={{ color: '#52c41a' }}
              prefix={<ClockCircleTwoTone twoToneColor="#52c41a" />}
              suffix={stats.totalTasks ? `/ ${stats.totalTasks}` : ''}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="完成率"
              value={stats.overallPercent}
              suffix="%"
              valueStyle={{ color: stats.overallPercent === 100 ? '#52c41a' : '#1677ff' }}
            />
          </Col>
        </Row>
        <Progress
          percent={stats.overallPercent}
          size="small"
          showInfo={false}
          strokeColor={{ '0%': '#722ed1', '100%': '#52c41a' }}
          style={{ marginTop: 8 }}
        />
      </Card>

      {/* 表格 */}
      <Card style={adminContentCardStyle} styles={{ body: { paddingTop: 12, paddingBottom: 12 } }}>
        {!batchId ? (
          <Empty description="请先选择批次" />
        ) : isLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin />
          </div>
        ) : filtered.length === 0 ? (
          <Empty description="没有符合条件的数据" />
        ) : (
          <Table<EvaluateeRow>
            rowKey="evaluateeId"
            dataSource={filtered}
            size="middle"
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `共 ${t} 名被评价人` }}
            columns={[
              {
                title: '被评价人',
                key: 'name',
                width: 160,
                render: (_v, r) => (
                  <Space size={8}>
                    <Avatar size={30} icon={<UserOutlined />} style={{ background: '#1677ff' }}>
                      {r.evaluateeName?.[0]}
                    </Avatar>
                    <span style={{ fontWeight: 600 }}>{r.evaluateeName}</span>
                  </Space>
                ),
              },
              {
                title: '部门',
                key: 'dept',
                ellipsis: true,
                render: (_v, r) => (
                  <Text style={{ fontSize: 13, color: '#555' }}>{shortDept(r.deptPath)}</Text>
                ),
              },
              {
                title: (
                  <Tooltip title="与单维度改分无关：表示 HR 对本人在本批次素质项评分整体核对完毕。可反复切换。">
                    <span>校准</span>
                  </Tooltip>
                ),
                key: 'rowCalibration',
                width: 104,
                align: 'center',
                render: (_v, r) => {
                  const done = calibrationByEvaluatee.get(r.evaluateeId) === true;
                  const rowLoading =
                    rowCalibrationMutation.isPending &&
                    rowCalibrationMutation.variables?.evaluateeId === r.evaluateeId;
                  return (
                    <Button
                      type={done ? 'primary' : 'default'}
                      size="small"
                      loading={rowLoading}
                      disabled={!batchId}
                      icon={
                        done ? (
                          <CheckCircleOutlined />
                        ) : (
                          <WarningOutlined style={{ color: '#fa8c16' }} />
                        )
                      }
                      onClick={() =>
                        rowCalibrationMutation.mutate({
                          evaluateeId: r.evaluateeId,
                          completed: !done,
                        })
                      }
                      style={
                        done
                          ? {
                              background: '#389e0d',
                              borderColor: '#389e0d',
                              fontWeight: 600,
                            }
                          : {
                              borderColor: '#fa8c16',
                              color: '#d46b08',
                              fontWeight: 600,
                            }
                      }
                    >
                      {done ? '已校准' : '待校准'}
                    </Button>
                  );
                },
              },
              {
                title: '人才池',
                key: 'pool',
                width: 110,
                render: (_v, r) =>
                  r.pool ? (
                    <Tag color={POOL_COLORS[r.pool] || 'default'}>{getTalentPoolLabel(r.pool)}</Tag>
                  ) : (
                    '-'
                  ),
              },
              {
                title: '应评',
                dataIndex: 'total',
                width: 80,
                align: 'center',
                render: (v: number) => <Tag color="processing">{v} 人</Tag>,
              },
              {
                title: '已提交',
                dataIndex: 'done',
                width: 80,
                align: 'center',
                render: (v: number) => <Tag color="success">{v}</Tag>,
              },
              {
                title: '待提交',
                dataIndex: 'pending',
                width: 80,
                align: 'center',
                render: (v: number) =>
                  v > 0 ? <Tag color="warning">{v}</Tag> : <Tag color="default">0</Tag>,
              },
              {
                title: '完成进度',
                dataIndex: 'percent',
                width: 220,
                sorter: (a, b) => a.percent - b.percent,
                defaultSortOrder: 'ascend',
                render: (v: number) => (
                  <Progress
                    percent={v}
                    size="small"
                    status={v === 100 ? 'success' : 'active'}
                    strokeColor={v === 100 ? '#52c41a' : '#1677ff'}
                  />
                ),
              },
              {
                title: '操作',
                key: 'action',
                width: 110,
                render: (_v, r) => (
                  <Button
                    type="link"
                    size="small"
                    icon={<FileSearchOutlined />}
                    onClick={() => setActiveRow(r)}
                  >
                    查看详情
                  </Button>
                ),
              },
            ]}
          />
        )}
      </Card>

      {/* 详情 Modal：列出该被评价人的所有评价人 + 关系 + 状态 + 提交耗时 */}
      <Modal
        open={!!activeRow}
        onCancel={() => setActiveRow(null)}
        footer={null}
        width={820}
        destroyOnClose
        title={
          activeRow ? (
            <Space size={8}>
              <Avatar size={30} icon={<UserOutlined />} style={{ background: '#1677ff' }}>
                {activeRow.evaluateeName?.[0]}
              </Avatar>
              <span style={{ fontSize: 16, fontWeight: 600 }}>{activeRow.evaluateeName}</span>
              <Text type="secondary" style={{ fontSize: 13 }}>
                {shortDept(activeRow.deptPath)}
              </Text>
              {activeRow.pool && (
                <Tag color={POOL_COLORS[activeRow.pool] || 'default'}>
                  {getTalentPoolLabel(activeRow.pool)}
                </Tag>
              )}
            </Space>
          ) : (
            '评价详情'
          )
        }
      >
        {activeRow && (
          <>
            <Card size="small" style={{ marginBottom: 12, background: '#f5f7ff' }}>
              <Space size="middle" wrap>
                <span>
                  应评价 <b>{activeRow.total}</b> 人
                </span>
                <span style={{ color: '#52c41a' }}>
                  已提交 <b>{activeRow.done}</b>
                </span>
                <span style={{ color: '#fa8c16' }}>
                  待提交 <b>{activeRow.pending}</b>
                </span>
                <Progress
                  percent={activeRow.percent}
                  size="small"
                  style={{ width: 180 }}
                  status={activeRow.percent === 100 ? 'success' : 'active'}
                />
              </Space>
            </Card>

            <Table<RawTask>
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={activeRow.tasks}
              expandable={{
                // 已提交的 task 默认折起，点开看具体维度评分；未提交的折叠状态显示「未提交」占位
                expandedRowRender: (t) => <ScoreBreakdown task={t} />,
                rowExpandable: () => true,
              }}
              columns={[
                {
                  title: '评价人',
                  key: 'evaluator',
                  width: 130,
                  render: (_v, t) => (
                    <Space size={6}>
                      <Avatar size={22} icon={<UserOutlined />}>
                        {t.evaluator?.name?.[0]}
                      </Avatar>
                      <span style={{ fontWeight: 500 }}>{t.evaluator?.name || '-'}</span>
                    </Space>
                  ),
                },
                {
                  title: '关系',
                  dataIndex: 'roleInEval',
                  width: 160,
                  render: (v: string, t) => (
                    <Tag color="blue">{getEvalRoleLabel(t.template?.pool, v)}</Tag>
                  ),
                },
                {
                  title: '状态',
                  dataIndex: 'status',
                  width: 90,
                  render: (v: string) => <Tag color={taskStatusColor(v)}>{taskStatusLabel(v)}</Tag>,
                },
                {
                  title: '总分',
                  key: 'total',
                  width: 90,
                  align: 'center',
                  render: (_v, t) => {
                    if (!isDone(t.status)) return <Text type="secondary">—</Text>;
                    const sum = (t.scores ?? []).reduce((s, r) => s + (r.score || 0), 0);
                    const dims = t.template?.dimensions?.length ?? (t.scores?.length ?? 0);
                    if (dims === 0) return <Text type="secondary">—</Text>;
                    return (
                      <Tag color="purple" style={{ fontWeight: 600 }}>
                        {sum} / {dims * 7}
                      </Tag>
                    );
                  },
                },
                {
                  title: '提交时间',
                  dataIndex: 'submitAt',
                  width: 160,
                  render: (v: string | null | undefined) =>
                    v ? new Date(v).toLocaleString('zh-CN', { hour12: false }) : '-',
                },
                {
                  title: '提交耗时',
                  dataIndex: 'submitDuration',
                  width: 100,
                  align: 'right',
                  render: (v: number | null | undefined) => {
                    if (v == null) return '-';
                    return v < 30 ? <Tag color="red">{v} 秒 · 可疑</Tag> : `${v} 秒`;
                  },
                },
              ]}
            />
          </>
        )}
      </Modal>
    </div>
  );
}

/**
 * 单条评价 task 的评分明细（用于 EvalTasksPage 详情 Modal 的 expandable row）。
 * 把后端返回的 EvaluationScore[] 与模板里的维度元数据 join 起来：
 *   - 已提交：列出每个维度（中文名）+ 0-7 分 + 评语
 *   - 未提交：明显占位提示，避免管理员误判已读到空数据
 */
function ScoreBreakdown({ task }: { task: RawTask }) {
  const submitted = isDone(task.status);
  const dims = (task.template?.dimensions ?? []) as TemplateDim[];
  const scoreMap = new Map<string, ScoreRow>(
    (task.scores ?? []).map((s) => [s.dimCode, s]),
  );
  // 优先按模板里维度顺序展示（业务直觉），无模板维度时退化为评分提交顺序
  const rows: { code: string; name: string; score: number | null; comment: string | null }[] =
    dims.length > 0
      ? dims.map((d) => {
          const sc = scoreMap.get(d.code);
          return {
            code: d.code,
            name: d.name || d.code,
            score: sc?.score ?? null,
            comment: sc?.comment ?? null,
          };
        })
      : (task.scores ?? []).map((s) => ({
          code: s.dimCode,
          name: s.dimCode,
          score: s.score,
          comment: s.comment ?? null,
        }));

  if (!submitted) {
    return (
      <div style={{ padding: '8px 12px', color: '#999' }}>该评价人尚未提交，暂无评分明细。</div>
    );
  }

  if (rows.length === 0) {
    return (
      <div style={{ padding: '8px 12px', color: '#999' }}>
        已提交但无评分记录（旧批次或数据异常，请联系管理员）。
      </div>
    );
  }

  return (
    <div style={{ padding: '6px 12px' }}>
      <Table
        size="small"
        rowKey={(r) => r.code}
        dataSource={rows}
        pagination={false}
        columns={[
          {
            title: '维度',
            dataIndex: 'name',
            width: 200,
            render: (v: string, r) => (
              <Space size={6}>
                <Tag color="default" style={{ margin: 0, fontFamily: 'monospace' }}>
                  {r.code}
                </Tag>
                <span style={{ fontWeight: 500 }}>{v}</span>
              </Space>
            ),
          },
          {
            title: '评分（0-7）',
            dataIndex: 'score',
            width: 130,
            align: 'center',
            render: (v: number | null) => {
              if (v == null) return <Text type="secondary">未评</Text>;
              const color = v >= 6 ? 'green' : v >= 4 ? 'gold' : v >= 2 ? 'orange' : 'red';
              return (
                <Tag color={color} style={{ fontWeight: 600, fontSize: 13 }}>
                  {v} 分
                </Tag>
              );
            },
          },
          {
            title: '评语',
            dataIndex: 'comment',
            render: (v: string | null) =>
              v ? (
                <span style={{ whiteSpace: 'pre-wrap', color: '#444' }}>{v}</span>
              ) : (
                <Text type="secondary">未填评语</Text>
              ),
          },
        ]}
      />
    </div>
  );
}
